import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './TabContent.css';
import ProjectsTabContent from './ProjectsTabContent';
import OverviewTabContent from './OverviewTabContent';
import ProtocolsTabContent from './ProtocolsTabContent';
import MyClubTabContent from './MyClubTabContent';
import FinanceTabContent from './FinanceTabContent';
import TicketsTabContent from './TicketsTabContent';
import CustomersTabContent from './CustomersTabContent';
import UpdatesTabContent from './UpdatesTabContent';
import SwagUpdated from './SwagUpdated';
import LogsTabContent from './LogsTabContent';
import NotificationList from './NotificationList';
import CustomerProfile from './CustomerProfile';
import AddUnitToCustomer from './AddUnitToCustomer';
import AddCustomerToUnit from './AddCustomerToUnit';
import GroupsTabContent from './GroupsTabContent';
import { useCachedPermissions } from '../hooks/useCachedPermissions';

const TabContent = ({ selectedTab, searchQuery }) => {
    const { hasShowNotification } = useCachedPermissions();
    const [lastTab, setLastTab] = useState('overview');
    const location = useLocation();
    const navigate = useNavigate();

    // Extract customer ID from URL if we're on customer profile tab
    const getCustomerIdFromUrl = () => {
        const pathParts = location.pathname.split('/').filter(part => part !== '');
        if (pathParts.length === 2 && pathParts[0] === 'customer') {
            return pathParts[1];
        }
        return null;
    };

    // Extract customer ID from URL if we're on add-unit-to-customer tab
    const getCustomerIdFromAddUnitUrl = () => {
        const pathParts = location.pathname.split('/').filter(part => part !== '');
        if (pathParts.length === 2 && pathParts[0] === 'add-unit') {
            return pathParts[1];
        }
        return null;
    };

    // Extract unit ID from URL if we're on add-customer-to-unit tab
    const getUnitIdFromAddCustomerUrl = () => {
        const pathParts = location.pathname.split('/').filter(part => part !== '');

        // Handle /add-customer/:unitId pattern
        if (pathParts.length === 2 && pathParts[0] === 'add-customer') {
            return pathParts[1];
        }

        // Handle /unit/:unitId/update-installments pattern
        if (pathParts.length === 3 && pathParts[0] === 'unit' && pathParts[2] === 'update-installments') {
            return pathParts[1];
        }

        return null;
    };

    useEffect(() => {
        if (selectedTab !== 'projects') {
            setLastTab(selectedTab);
        }
    }, [selectedTab]);

    const getTabContent = () => {
        if (selectedTab === 'projects') {
            return {
                title: 'Projects',
            };
        }
        switch (selectedTab) {
            case 'overview':
                return {
                    title: 'Overview',
                };
            case 'protocols':
                return {
                    title: 'Protocols',
                };
            case 'tickets':
                return {
                    title: 'Tickets',
                };
            case 'finance':
                return {
                    title: 'Finance',
                };
            case 'maintenance':
                return {
                    title: 'Maintenance',
                };
            case 'utility':
                return {
                    title: 'Utility',
                };
            case 'logs':
                return {
                    title: 'logs',
                };
            case 'customers':
                return {
                    title: 'Customers',
                };
            case 'groups':
                return {
                    title: 'Groups',
                };
            case 'myclub':
                return {
                    title: 'My Club',
                };
            case 'updates':
                return {
                    title: 'Updates',
                };
            case 'customer-profile':
                return {
                    title: 'Customer Profile',
                };
            case 'add-unit-to-customer':
                return {
                    title: 'Add Unit to Customer',
                };
            case 'add-customer-to-unit':
                return {
                    title: 'Add Customer to Unit',
                };
            default:
                return {
                    title: 'Overview',
                };
        }
    };

    const { title, content } = getTabContent();

    const shouldShowProjectsContent = selectedTab === 'projects';
    const shouldShowFinanceContent = selectedTab === 'finance' ||
        selectedTab === 'finance-paid' ||
        selectedTab === 'finance-unpaid' ||
        selectedTab === 'maintenance' ||
        selectedTab === 'maintenance-paid' ||
        selectedTab === 'maintenance-unpaid' ||
        selectedTab === 'utility' ||
        selectedTab === 'utility-paid' ||
        selectedTab === 'utility-unpaid';

    const shouldShowTicketsContent = selectedTab === 'tickets' ||
        selectedTab === 'tickets-requests' ||
        selectedTab === 'tickets-closed' ||
        selectedTab === 'tickets-categories';

    const shouldShowCustomersContent = selectedTab === 'customers';
    const shouldShowGroupsContent = selectedTab === 'groups';
    const shouldShowCustomerProfileContent = selectedTab === 'customer-profile';
    const shouldShowAddUnitToCustomerContent = selectedTab === 'add-unit-to-customer';
    const shouldShowAddCustomerToUnitContent = selectedTab === 'add-customer-to-unit';
    const shouldShowUpdatesContent = selectedTab === 'updates';
    const shouldShowLogsContent = selectedTab === 'logs' ||
        selectedTab === 'logs-admin' ||
        selectedTab === 'logs-user';
    const shouldShowNotificationsContent = selectedTab === 'notifications';

    return (
        <div className="tab-content">
            {shouldShowProjectsContent ? (
                <ProjectsTabContent key={selectedTab} selectedTab={selectedTab} />
            ) : shouldShowFinanceContent ? (
                <FinanceTabContent selectedTab={selectedTab} />
            ) : shouldShowTicketsContent ? (
                <TicketsTabContent selectedTab={selectedTab} />
            ) : shouldShowCustomersContent ? (
                <CustomersTabContent selectedTab={selectedTab} />
            ) : shouldShowGroupsContent ? (
                <GroupsTabContent selectedTab={selectedTab} />
            ) : shouldShowCustomerProfileContent ? (
                <CustomerProfile
                    customerId={getCustomerIdFromUrl()}
                    onBack={() => navigate('/customers')}
                    onEdit={() => { }}
                    onDelete={() => navigate('/customers')}
                />
            ) : shouldShowAddUnitToCustomerContent ? (
                <AddUnitToCustomer
                    customerId={getCustomerIdFromAddUnitUrl()}
                    onCancel={() => navigate('/customers')}
                />
            ) : shouldShowAddCustomerToUnitContent ? (
                <AddCustomerToUnit
                    unitId={getUnitIdFromAddCustomerUrl()}
                    onCancel={() => navigate(-1)} // Simply go back one step in browser history
                    onSave={() => navigate(-1)} // Go back after successful save
                />
            ) : shouldShowLogsContent ? (
                <LogsTabContent selectedTab={selectedTab} />
            ) : shouldShowNotificationsContent ? (
                hasShowNotification ? (
                    <NotificationList />
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        color: '#ccc'
                    }}>
                        <h3>Access Denied</h3>
                        <p>You do not have permission to view notifications.</p>
                    </div>
                )
            ) : selectedTab === 'updates' ? (
                <SwagUpdated />
            ) : shouldShowUpdatesContent ? (
                <UpdatesTabContent selectedTab={selectedTab} />
            ) : selectedTab === 'protocols' ? (
                <ProtocolsTabContent />
            ) : selectedTab === 'myclub' ? (
                <MyClubTabContent />
            ) : (
                <>
                    {title === 'Overview' ? (
                        <OverviewTabContent />
                    ) : (
                        <div>
                            <h2 className="tab-title">{title}</h2>
                            {content}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TabContent; 